(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  /**
   * ScrollBody manages the physics and state of a scrollable carousel body.
   * It handles velocity, direction, and smooth scrolling transitions based on provided state.
   *
   * @class
   * @example
   * const scrollBody = new ScrollBody().initialize(state);
   * scrollBody.seek();
   *
   * @property {number} scrollVelocity - The current velocity of the scroll.
   * @property {number} scrollDirection - The current direction of the scroll (-1, 0, or 1).
   * @property {object} state - The state object containing scroll parameters and locations.
   * @property {number} rawLocation - The current raw (unrounded) scroll location.
   * @property {number} rawLocationPrevious - The previous raw scroll location.
   *
   * @method initialize
   * Initializes the ScrollBody with a given state object.
   * @param {object} state - The state object with getters and setters for scroll properties.
   * @returns {ScrollBody} The instance for chaining.
   *
   * @method seek
   * Advances the scroll position based on velocity, friction, and target.
   * Updates internal state and direction.
   * @returns {ScrollBody} The instance for chaining.
   *
   * @method settled
   * Determines if the scroll position is close enough to the target to be considered settled.
   * @returns {boolean} True if settled, false otherwise.
   *
   * @method direction
   * Gets the current scroll direction.
   * @returns {number} The scroll direction (-1 for left/up, 1 for right/down, 0 for none).
   */
  class ScrollBody {
    constructor() {
      this.scrollVelocity = 0;
      this.scrollDirection = 0;
    }

    /**
     * Initializes the carousel scroll body with the provided state.
     *
     * @param {Object} state - The state object used to initialize the scroll body.
     * @returns {this} The current instance for chaining.
     */
    initialize(state) {
      this.state = state;
      this.rawLocation = this.state.get('location').get();
      this.rawLocationPrevious = 0;
      return this;
    }

    /**
     * Updates the scroll position towards the target location, applying velocity and friction.
     * Handles both instant and animated scrolling based on the configured duration.
     *
     * @returns {this} Returns the current instance for chaining.
     */
    seek() {
      const target = this.state.get('target');
      const location = this.state.get('location');
      const displacement = target.get() - location.get();
      const scrollDuration = this.state.get('duration');
      const scrollFriction = this.state.get('friction');
      const previousLocation = this.state.get('previousLocation');

      const isInstant = !scrollDuration;
      let scrollDistance = 0;

      if (isInstant) {
        this.scrollVelocity = 0;
        previousLocation.set(target.get());
        location.set(target.get());
        scrollDistance = displacement;
      } else {
        previousLocation.set(location.get());
        this.scrollVelocity += displacement / scrollDuration;
        this.scrollVelocity *= scrollFriction;
        this.rawLocation += this.scrollVelocity;
        location.add(this.scrollVelocity);
        scrollDistance = this.rawLocation - this.rawLocationPrevious;
      }

      this.scrollDirection = Math.sign(scrollDistance);
      this.rawLocationPrevious = this.rawLocation;

      return this;
    }

    /**
     * Determines if the scroll position has settled within a negligible threshold.
     *
     * @returns {boolean} Returns true if the difference between the target position and
     * the current offset location is less than 0.001, indicating the scroll has settled.
     */
    settled() {
      const target = this.state.get('target');
      const offsetLocation = this.state.get('offsetLocation');
      const diff = target.get() - offsetLocation.get();
      return Math.abs(diff) < 0.001;
    }

    /**
     * Determines whether the carousel can scroll to the next item.
     *
     * @returns {boolean} True if scrolling to the next item is allowed, either because looping is enabled
     *                    or the next index is different from the current index; otherwise, false.
     */
    canScrollNext() {
      const nextIndex = this.state.get('index').add(1).get();
      const currentIndex = this.state.get('index').get();
      const loop = this.state.get('loop');
      const canScroll = loop || nextIndex !== currentIndex;
      return canScroll;
    }

    /**
     * Determines whether the carousel can scroll to the previous item.
     *
     * Checks if the carousel is in loop mode or if there is a previous item available
     * based on the current index. Returns true if scrolling to the previous item is possible.
     *
     * @returns {boolean} True if the carousel can scroll to the previous item, otherwise false.
     */
    canScrollPrevious() {
      const previousIndex = this.state.get('index').add(-1).get();
      const currentIndex = this.state.get('index').get();
      const loop = this.state.get('loop');
      const canScroll = loop || previousIndex !== currentIndex;
      return canScroll;
    }

    /**
     * Returns the current scroll direction of the carousel.
     * @returns {string} The scroll direction.
     */
    direction() {
      return this.scrollDirection;
    }
  }

  global.NewHomeSource.Carousel.ScrollBody = ScrollBody;
}(window));
